import { DateString, useDateString } from "client/Components";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	AppointmentSpanInfo,
	AppointmentSpanInput,
	calendarLogic
} from "client_legacy/Workspace/LiveInformation/Calendar/calendarLogic";
import { localize } from "client_legacy/ndaw/culture";
import { DateFormat } from "client_legacy/ndaw/dateFormatting";
import { ReactElement } from "react";

export type AppointmentSpanProps = {
	appointment: AppointmentSpanInput;
	timezone: TimeZone;
};

export const AppointmentSpan = (props: AppointmentSpanProps): ReactElement => {
	const {
		appointment,
		timezone
	} = props;

	const {
		start,
		end,
		isAllDayEvent,
		formats: {
			startFormat,
			endFormat
		}
	} = getSpanData(appointment, timezone);

	return (
		<>
			<DateString
				value={start}
				format={startFormat}
				timezone={timezone}
			/>
			{ endFormat && (
				<>
					{" - "}
					<DateString
						value={end}
						format={endFormat}
						timezone={timezone}
					/>
				</>
			)}
			{ isAllDayEvent && (
				<>
					{` ${localize("_AllDay").toLowerCase()}`}
				</>
			)}
		</>
	);
};
AppointmentSpan.displayName = "AppointmentSpan";

export function useAppointmentSpan(
	appointment: AppointmentSpanInput,
	timezone: TimeZone
): string {
	const {
		start,
		end,
		isAllDayEvent,
		formats: {
			startFormat,
			endFormat
		}
	} = getSpanData(appointment, timezone);

	const startDateString: string = useDateString(start, startFormat, timezone);
	const endDateString: string = useDateString(end, endFormat ?? "AbsoluteTimestamp", timezone);

	return `${startDateString}${endFormat ? ` - ${endDateString}` : ""}${
		isAllDayEvent ? ` ${localize("_AllDay").toLowerCase()}` : ""
	}`;
}

type StartAndEndDatesFormat = {
	startFormat: DateFormat;
	endFormat: DateFormat | undefined;
};

type AppointmentSpanData = {
	start: Date;
	end: Date;
	isAllDayEvent: boolean;
	formats: StartAndEndDatesFormat;
};

function getSpanData(
	appointment: AppointmentSpanInput,
	timezone: TimeZone
): AppointmentSpanData {
	const spanInfo: AppointmentSpanInfo = calendarLogic.getAppointmentSpanInfo(appointment, timezone);

	const startDate: DateOnly = convertToDateOnly(spanInfo.startDateToCompare, timezone);

	const endDate: DateOnly = convertToDateOnly(
		new Date(spanInfo.endDateToCompare.getTime() - 1),
		timezone
	);

	const isSingleDay: boolean = startDate.isEqualTo(endDate);

	const { isAllDayEvent } = appointment;

	const formats: StartAndEndDatesFormat = getDateFormat(isAllDayEvent, isSingleDay);

	return {
		start: spanInfo.startDateToCompare,
		end: spanInfo.endDateToCompare,
		formats,
		isAllDayEvent
	};
}

function getDateFormat(isAllDayEvent: boolean, isSingleDay: boolean): StartAndEndDatesFormat {
	const startFormat = isAllDayEvent ? "DMY" : "DMYT";
	const endFormat = isAllDayEvent
		? isSingleDay ? undefined : "DMY"
		: isSingleDay ? "T" : "DMYT";

	return { startFormat, endFormat };
}
